import {
    Box,
    Button,
    CircularProgress,
    DialogActions,
    DialogContent,
    DialogContentText,
    DialogTitle,
    Divider,
    Link,
    MenuItem,
    Paper,
    Select,
    Stack,
    TextField,
    Typography,
} from '@mui/material';
import { useOktaAuth } from '@okta/okta-react';
import React, { useEffect, useState } from 'react';
import Backend, { InjectInfo } from '../backend/Backend';
import { SalesforceApp, SalesforceDashboard } from '../common/types';
import IconLink from '../components/IconLink';
import SiDialog from '../components/SiDialog';
import { ReactComponent as AlertIcon } from '../resources/images/alert.svg';
import AquantCancelIcon from '../resources/images/aq-cancel.svg';
import { ReactComponent as UrlLinkIcon } from '../resources/images/link.svg';
import { ReactComponent as SelectExistingAppIcon } from '../resources/images/select_app_icon.svg';
import { ConfigStore, handleRunConfigProps } from '../stores/ConfigStore';
import Util from '../Util';
import {
    appendPkceToUrl,
    generateCodeChallenge,
    generateCodeVerifier,
    generatePkceNonce,
    sfdcOAuthCallbackUrl,
    storePkceVerifierForNonce,
} from '../sfdc/pkce';
import './ProcessDialog.scss';

type Props = {
    configStore: ConfigStore;
    onClose?: Function;
};

function ProcessDialog({ configStore, onClose }: Props) {
    const configId = configStore.parent_config_id;
    const { parent_config_id } = configStore;
    const {
        handleRunConfig,
        processSuccessCallback, // triggers when excecution succeeds
        processFailCallback, // triggers when excecution fails
    } = configStore;
    const { authState, oktaAuth } = useOktaAuth();
    const [step, setStep] = useState<number>(1);
    const [existingApp, setExistingApp] = useState<boolean>(false);
    const [orgApps, setOrgApps] = useState<SalesforceApp[]>([]);
    const [selectedApp, setSelectedApp] = useState<SalesforceApp | undefined>(undefined);
    const [injectInfo, setInjectInfo] = useState<InjectInfo | undefined>(undefined);
    const [appDashboards, setAppDashboards] = useState<SalesforceDashboard[]>([]);
    const [selectedDashboard, setSelectedDashboard] = useState<SalesforceDashboard | undefined>(undefined);
    const [loading, setLoading] = useState<boolean>(false);
    const [installedPackageVersion, setInstalledPackageVersion] = useState('');
    const [latestPackageVersion, setLatestPackageVersion] = useState('');
    const totalSteps = configStore.injectOnly ? 2 : 3;
    enum OrgType {
        production = 'login.salesforce.com',
        sandbox = 'test.salesforce.com',
        custom = 'custom',
    }
    const [orgType, setOrgType] = useState(OrgType.production);

    const progress = (
        <div style={{ display: 'flex', padding: 12, position: 'relative', marginRight: 23 * (totalSteps - 1) }}>
            {Array.from(Array(totalSteps).keys()).map((_, i) => (
                <>
                    <div
                        key={i}
                        style={{
                            position: 'absolute',
                            height: 8,
                            width: 8,
                            border: '0.08px solid #2468FF',
                            borderRadius: 50,
                            left: 23 * i,
                            background: i === step - (configStore.injectOnly ? 1 : 0) - 1 ? '#2468FF' : undefined,
                        }}
                    />
                    {i < Array.from(Array(totalSteps).keys()).length - 1 && (
                        <div
                            style={{
                                position: 'absolute',
                                borderTop: _ >= i ? '0.08em solid #2468FF' : undefined,
                                width: 15,
                                left: 8 + 23 * i,
                                marginTop: 3,
                            }}
                        />
                    )}
                </>
            ))}
        </div>
    );

    const MenuProps = {
        PaperProps: {
            sx: {
                borderRadius: '6px',
                boxShadow: '0px 2px 10px rgba(49, 63, 82, 0.211593)',
            },
        },
        MenuListProps: {
            sx: {
                '& li:hover': {
                    background: '#EDEFF5',
                },
                '& li.Mui-selected': {
                    fontWeight: 500,
                    color: '#1F1F1F',
                    background: 'white',
                },
                '& li.Mui-selected:hover': {
                    background: '#EDEFF5',
                },
            },
        },
    };

    const getContentByStep = () => {
        switch (step) {
            case 1:
                return <FirstStepContent />;
            case 2:
                return <SecondStepContent />;
            case 3:
                return <ThirdStepContent />;
            default:
                break;
        }
    };

    const handleClose = () => {
        setStep(1);
        setExistingApp(false);
        setSelectedApp(undefined);
        setSelectedDashboard(undefined);
        setOrgApps([]);
        setLoading(false);
        if (onClose) {
            onClose();
        }
    };

    const initiateSFLogin = (config_id: string) => {
        (async () => {
            console.info('[ProcessDialog] SFDC login started', { config_id, orgType });
            const access_token_url =
                'https://{0}/services/oauth2/authorize?client_id={1}&redirect_uri={2}' +
                '&response_type=code&display=popup&prompt=login+consent&state={3}';

            const pkceNonce = generatePkceNonce();
            const verifier = generateCodeVerifier();
            const challenge = await generateCodeChallenge(verifier);
            storePkceVerifierForNonce(pkceNonce, verifier);

            let loginUrl = access_token_url
                .replace('{0}', orgType)
                .replace(
                    '{1}',
                    (window.REACT_APP_CONFIG?.REACT_APP_SFDC_APP_CLIENT_ID as string) ||
                    process.env.REACT_APP_SFDC_APP_CLIENT_ID,
                )
                .replace('{2}', `${window.location.origin}/sfdc/target/oauth2/callback`)
                .replace('{3}', `${orgType},default,${pkceNonce}`);

            loginUrl = appendPkceToUrl(loginUrl, challenge);

            const win = Util.popupWindow(loginUrl, 'Service Insights - Target', false);
            (window as any).setLoginResponse = async function (
                auth_code: string,
                domain: string,
                _instance_url?: string,
                code_verifier?: string,
            ) {
                try {
                    setLoading(true);
                    (window as any).setLoginResponse = () => {};
                    win!.close();
                    const org_details = await processSFLogin(config_id, auth_code, domain, code_verifier);

                    console.info('[ProcessDialog] SFDC login ok', {
                        user: org_details.username,
                        instance: org_details.instance_url,
                    });

                    const org_data = {
                        app_name: '',
                        instance_url: org_details.instance_url,
                        access_token: org_details.access_token,
                    };

                    setInjectInfo(org_data as InjectInfo);

                    const apps = await Backend.getOrgApps(org_data as InjectInfo, oktaAuth);

                    setOrgApps(apps.data as unknown as SalesforceApp[]);

                    const installedVer = await Backend.getInstalledPackageVersion(org_data as InjectInfo, oktaAuth);
                    const latestVer = await Backend.getLatestPackageVersion(oktaAuth);

                    setInstalledPackageVersion(installedVer);
                    setLatestPackageVersion(latestVer);
                    setLoading(false);
                    setStep((currentStep) => currentStep + 1);
                } catch (err) {
                    console.error('[ProcessDialog] SFDC login failed', err);
                    setLoading(false);
                }
            };
        })().catch((err) => {
            console.error('[ProcessDialog] SFDC login init failed', err);
        });
    };

    async function processSFLogin(
        config_id: string,
        auth_code: string,
        domain: string,
        code_verifier?: string,
    ) {
        const resp = await Backend.login(
            config_id,
            auth_code,
            domain,
            'Target',
            oktaAuth,
            code_verifier,
            sfdcOAuthCallbackUrl('target'),
        );
        return resp.data;
    }

    const handleAppSelected = async (e: { target: { value: string } }) => {
        setAppDashboards([]);
        setSelectedDashboard(undefined);
        setLoading(true);
        const app = orgApps.find((app) => app.id === e.target.value);
        if (app) {
            app.version = latestPackageVersion; // app version is always latest
        }
        setSelectedApp(app);
        setInjectInfo((prev) => ({
            ...prev,
            app_name: app.label,
        }));
        // get app dashboards
        const dashboards = await Backend.getAppDashboards(injectInfo, app.id, oktaAuth);
        const dashboards_list = dashboards.data as unknown as SalesforceDashboard[];
        setAppDashboards(dashboards.data as unknown as SalesforceDashboard[]);
        if (dashboards_list.length === 1) {
            setSelectedDashboard(dashboards_list[0]);
        }
        setLoading(false);
    };

    const handleDashboardSelected = async (e) => {
        const dashboard = appDashboards.find((dashboard) => dashboard.id === e.target.value);
        setSelectedDashboard(dashboard);
    };

    const handleProcessOnly = () => {
        setLoading(true);
        const handleRunConfigProps: handleRunConfigProps = {
            isEdit: parent_config_id !== '',
            oktaAuth,
            success_callback: processSuccessCallback && processSuccessCallback(),
            fail_callback: processFailCallback && processFailCallback(),
            processorOnly: configStore.processOnlyMode,
            isPartial: configStore.isPartial,
            partialMode: configStore.partialMode,
            processToDB: configStore.processToDB,
        };
        console.log({ handleRunConfigProps });
        handleRunConfig(handleRunConfigProps).then(() => handleClose());
    };

    const handleProcessInject = (newAppName?: string) => {
        setLoading(true);
        const newSelectedApp = selectedApp;
        const handleRunConfigProps: handleRunConfigProps = {
            isEdit: parent_config_id !== '',
            oktaAuth,
            success_callback: processSuccessCallback && processSuccessCallback(),
            fail_callback: processFailCallback && processFailCallback && processFailCallback(),
            inject_info: injectInfo,
            selected_app: newSelectedApp,
            selected_dashboard: selectedDashboard,
            newAppName,
            processorOnly: configStore.processOnlyMode,
            isPartial: configStore.isPartial,
            partialMode: configStore.partialMode,
            processToDB: configStore.processToDB,
        };
        console.log({ handleRunConfigProps });
        handleRunConfig(handleRunConfigProps).then(() => handleClose());
    };

    const FirstStepContent = () => (
        <>
            <DialogTitle>
                <Stack className="DialogTitle">
                    {progress}
                    <Typography className="DialogTitleText">Update a dashboard?</Typography>
                    <img
                        src={AquantCancelIcon}
                        onClick={() => {
                            handleClose();
                        }}
                        alt="cancel icon"
                        className="CancelIcon"
                    />
                </Stack>
            </DialogTitle>
            <Divider />
            <Paper className="DialogContentPaper">
                {!configStore.processToDB && (
                    <DialogContent className="ProcessDialogContent">
                        <DialogContentText className="DialogContentTextBold">
                            Do you want to update the data of a dashboard after processing?
                        </DialogContentText>
                        <DialogContentText gutterBottom className="DialogContentTextNormal">
                            If so, the processed data will be automaticated injected in the app that you will define in
                            the next step.
                        </DialogContentText>
                        <OutdatedAlert />
                    </DialogContent>
                )}
                {configStore.processToDB ? (
                    <DialogContent className="ProcessDialogContent">
                        <DialogContentText className="DialogContentTextNormal">
                            To enable Service Co-Pilot, the data should be re-processed. In order to ensure consistency
                            between Service Co-Pilot and the Dashboard, we recommend you to refresh the dashboard as
                            well.
                        </DialogContentText>
                        <DialogContentText gutterBottom className="DialogContentTextBold">
                            Do you want to refresh data on a dashboard after processing?
                        </DialogContentText>
                        <OutdatedAlert />
                    </DialogContent>
                ) : null}
                <DialogActions>
                    {loading ? (
                        <CircularProgress data-testid="CircularProgress" sx={{ marginRight: 2 }} size={20} />
                    ) : null}
                    <Button
                        data-testid="process-dialog-process-only-btn"
                        onClick={() => handleProcessOnly()}
                        disabled={loading}
                        disableRipple
                        className="DialogActionBtn"
                    >
                        Only process data
                    </Button>
                    <Button
                        data-testid="process-dialog-process-inject-btn"
                        onClick={() =>
                            /*setStep(2), change it to go into step 2 instead of initiateSFLogin*/ setStep(
                            (currentStep) => currentStep + 1,
                        )
                        }
                        disabled={loading}
                        variant="contained"
                        disableRipple
                        className="DialogActionBtnContained"
                    >
                        Yes, Update it!
                    </Button>
                </DialogActions>
            </Paper>
        </>
    );

    const SecondStepContent = () => (
        <>
            <DialogTitle>
                <Stack className="DialogTitle">
                    {progress}
                    <Typography className="DialogTitleText">Connect to Salesforce</Typography>
                    <img
                        src={AquantCancelIcon}
                        onClick={() => {
                            handleClose();
                        }}
                        alt="cancel icon"
                        className="CancelIcon"
                    />
                </Stack>
            </DialogTitle>
            <Divider />
            <Paper className="DialogContentPaper">
                {!configStore.processToDB && (
                    <DialogContent className="ProcessDialogContent">
                        <DialogContentText className="DialogContentTextBold">
                            Please select the org type, and connect to your account:
                        </DialogContentText>
                        <Stack className="AppDetailsContent" direction="row" alignItems="center" marginBottom="0px">
                            <Stack className="AppDetailsStack">
                                <Stack className="AppListdropsStack">
                                    <Stack direction="row" alignItems="center">
                                        <Typography width="86px">Org Type</Typography>
                                        <Select
                                            className="AppSelect OrgSelect"
                                            value={orgType}
                                            onChange={(event) => {
                                                const ot = event.target.value as OrgType;
                                                setOrgType(ot);
                                            }}
                                            IconComponent={null}
                                            MenuProps={MenuProps}
                                        >
                                            <MenuItem value={OrgType.production}>Production</MenuItem>
                                            <MenuItem value={OrgType.sandbox}>Sandbox</MenuItem>
                                            <MenuItem value={OrgType.custom}>Custom</MenuItem>
                                        </Select>
                                    </Stack>
                                </Stack>
                            </Stack>
                        </Stack>
                    </DialogContent>
                )}
                <DialogActions>
                    {loading ? (
                        <CircularProgress data-testid="CircularProgress" sx={{ marginRight: 2 }} size={20} />
                    ) : null}
                    {!configStore.injectOnly && (
                        <Button
                            data-testid="process-dialog-process-only-btn"
                            onClick={() => handleProcessOnly()}
                            disabled={loading}
                            disableRipple
                            className="DialogActionBtn"
                        >
                            Only process data
                        </Button>
                    )}
                    <Button
                        data-testid="process-dialog-process-inject-btn"
                        onClick={() => /*setStep(2)*/ initiateSFLogin(configId)}
                        disabled={loading}
                        variant="contained"
                        disableRipple
                        className="DialogActionBtnContained"
                    >
                        Login to Salesforce
                    </Button>
                </DialogActions>
            </Paper>
        </>
    );

    const ThirdStepContent = () => {
        const [newAppName, setNewAppName] = useState<string>('');
        const handleNewAppNameChange = (event: React.ChangeEvent<HTMLInputElement>) => {
            const newName = event.target.value;
            setNewAppName(newName);
        };
        const isPublishDisabled =
            loading || (!existingApp && !newAppName) || (existingApp && (!selectedApp || !selectedDashboard));

        return (
            <>
                <DialogTitle>
                    <Stack className="DialogTitle">
                        {progress}
                        <Typography className="DialogTitleText">
                            {existingApp ? 'Select an app' : 'Name new app'}
                        </Typography>
                        <img
                            src={AquantCancelIcon}
                            onClick={() => {
                                handleClose();
                            }}
                            alt="cancel icon"
                            className="CancelIcon"
                        />
                    </Stack>
                </DialogTitle>
                <Divider />
                <Paper className="DialogContentPaper">
                    <DialogContent className="ProcessDialogContent">
                        {existingApp ? (
                            <Box mb="20px">
                                <Link
                                    className="GoBackLink"
                                    color="inherit"
                                    onClick={() => {
                                        setSelectedApp(undefined);
                                        setSelectedDashboard(undefined);
                                        setExistingApp(false);
                                    }}
                                >{`< Name new app`}</Link>
                            </Box>
                        ) : null}
                        <DialogContentText className="DialogContentTextBold">
                            {existingApp
                                ? 'Please select the app and dashboard:'
                                : 'Please define a name for your app.'}
                        </DialogContentText>
                        <DialogContentText gutterBottom className="DialogContentTextNormal">
                            This project will be automatically assigned to this beta app for the next refreshes.
                        </DialogContentText>
                        <Stack className="AppDetailsContent">
                            {existingApp ? (
                                <Stack className="AppDetailsStack">
                                    <Stack className="AppListdropsStack">
                                        <Stack direction="row" alignItems="center" marginBottom="20px">
                                            <Typography width="86px">App</Typography>
                                            {/*<select className={`AppSelect ${!selectedApp && 'SelectItalic'}`} value={selectedApp && selectedApp.id} onChange={handleAppSelected}>
                                        <option value="" disabled selected hidden>Select a value</option>
                                        {orgApps.map((app)=><option key={app.id} value={app.id}>{app.name}</option>)}
                                    </select> */}
                                            <Select
                                                className={`AppSelect ${!selectedApp && 'SelectItalic'}`}
                                                value={selectedApp ? selectedApp.id : null}
                                                onChange={handleAppSelected}
                                                displayEmpty
                                                inputProps={{
                                                    'data-testid': 'process-dialog-app-select',
                                                }}
                                                renderValue={(selected) => {
                                                    if (selectedApp === undefined) {
                                                        return <em>Select an app</em>;
                                                    }

                                                    return orgApps.find((app) => app.id === selected)?.name;
                                                }}
                                                IconComponent={null}
                                                sx={{
                                                    fieldset: {
                                                        borderColor:
                                                            (+installedPackageVersion < +latestPackageVersion ||
                                                                (selectedApp &&
                                                                    +selectedApp.version < +latestPackageVersion)) &&
                                                            '#F4AC48',
                                                    },
                                                }}
                                                MenuProps={MenuProps}
                                            >
                                                {orgApps.map((app) => (
                                                    <MenuItem key={app.id} value={app.id}>
                                                        {app.name}
                                                    </MenuItem>
                                                ))}
                                            </Select>
                                        </Stack>
                                        <Stack direction="row" alignItems="center">
                                            <Typography width="86px">Dashboard</Typography>
                                            {/* <select disabled={appDashboards.length === 0} className={`AppSelect ${!selectedDashboard && 'SelectItalic'}`} value={selectedDashboard && selectedDashboard.id} onChange={handleDashboardSelected}>
                                        <option value="" disabled selected hidden>{loading ? 'Loading...' : 'Select a value'}</option>
                                        {appDashboards.map((dashboard)=><option key={dashboard.id} value={dashboard.id}>{dashboard.name}</option>)}
                                    </select> */}
                                            <Select
                                                className={`AppSelect ${!selectedApp && 'SelectItalic'}`}
                                                disabled={appDashboards.length === 0}
                                                value={selectedDashboard ? selectedDashboard.id : null}
                                                onChange={handleDashboardSelected}
                                                displayEmpty
                                                inputProps={{
                                                    'data-testid': 'process-dialog-app-dashboard-select',
                                                }}
                                                renderValue={(selected) => {
                                                    if (selectedDashboard === undefined) {
                                                        return <em>{loading ? 'Loading...' : 'Select a dashboard'}</em>;
                                                    }

                                                    return appDashboards.find((dashboard) => dashboard.id === selected)
                                                        ?.name;
                                                }}
                                                IconComponent={null}
                                                MenuProps={MenuProps}
                                            >
                                                {appDashboards.map((dashboard) => (
                                                    <MenuItem key={dashboard.id} value={dashboard.id}>
                                                        {dashboard.name}
                                                    </MenuItem>
                                                ))}
                                            </Select>
                                            {loading && appDashboards.length === 0 ? (
                                                <CircularProgress
                                                    data-testid="CircularProgress"
                                                    sx={{ marginLeft: 2 }}
                                                    size={20}
                                                />
                                            ) : null}
                                        </Stack>
                                    </Stack>
                                </Stack>
                            ) : (
                                <Stack className="AppDetailsStack">
                                    <Typography>App</Typography>
                                    <TextField
                                        key="new-app-name"
                                        size="small"
                                        placeholder="Type app name"
                                        className="AppTextBox"
                                        InputProps={{
                                            style: { height: '38px' },
                                        }}
                                        // eslint-disable-next-line react/jsx-no-duplicate-props
                                        inputProps={{
                                            'data-testid': 'process-dialog-new-app-textfield',
                                        }}
                                        sx={{
                                            fieldset: {
                                                borderColor:
                                                    +installedPackageVersion < +latestPackageVersion && '#F4AC48',
                                            },
                                        }}
                                        onChange={handleNewAppNameChange}
                                        value={newAppName}
                                        disabled={loading}
                                    />
                                    <IconLink
                                        disabled={orgApps.length === 0}
                                        onClick={() => !loading && setExistingApp(true)}
                                        SvgIcon={SelectExistingAppIcon}
                                        testId="process-dialog-existing-app-btn"
                                    >
                                        <Typography fontWeight={500}>Select existing app</Typography>
                                    </IconLink>
                                </Stack>
                            )}
                        </Stack>
                        <OutdatedAlert />
                    </DialogContent>
                    <DialogActions>
                        {loading && ((existingApp && selectedDashboard) || !existingApp) ? (
                            <CircularProgress data-testid="CircularProgress" sx={{ marginRight: 2 }} size={20} />
                        ) : null}
                        <Button
                            data-testid="process-dialog-process-only-btn"
                            onClick={() => handleProcessOnly()}
                            disabled={loading}
                            disableRipple
                            className="DialogActionBtn"
                        >
                            Only process data
                        </Button>
                        <Button
                            data-testid="process-dialog-publish-btn"
                            onClick={() => handleProcessInject(!existingApp ? newAppName : undefined)}
                            disabled={isPublishDisabled}
                            variant="contained"
                            disableRipple
                            className="DialogActionBtnContained"
                        >
                            Publish
                        </Button>
                    </DialogActions>
                </Paper>
            </>
        );
    };

    const OutdatedAlert = () => {
        const packageOutdated = +installedPackageVersion < +latestPackageVersion;
        const appOutdated = !packageOutdated && selectedApp && +selectedApp.version < +latestPackageVersion;

        return packageOutdated || appOutdated ? (
            <Stack direction="row" color="#4A4A4A">
                <div>
                    <AlertIcon style={{ height: '24px', marginRight: '12px' }} />
                </div>
                <Stack>
                    {packageOutdated ? (
                        <Typography>
                            The Service Insights package is outdated. The refresh will install and upgrade the app to
                            the latest version. To simply refresh the data:
                        </Typography>
                    ) : null}
                    {appOutdated ? (
                        <Typography>
                            The app is outdated. The refresh will upgrade the app to the latest version. To simply
                            refresh the data:
                        </Typography>
                    ) : null}
                    <Stack marginTop="10px">
                        <li>Choose “Only process data”</li>
                        <li>Download the output (.csv file) from the Summary or Projects Manager</li>
                        <li>
                            Upload the CSV file to <b style={{ fontWeight: 500 }}>legacy SID</b>
                            <Link target="_blank" href="https://aq-si-fe.herokuapp.com/" marginLeft="8px">
                                <UrlLinkIcon />
                            </Link>
                        </li>
                    </Stack>
                </Stack>
            </Stack>
        ) : (
            <></>
        );
    };

    useEffect(() => {
        if (step === 1 && configStore.injectOnly) {
            setStep(2);
        }
    });

    return (
        <SiDialog open={configStore.processDialogOpen} onClose={() => handleClose()}>
            {configStore.processDialogOpen ? getContentByStep() : null}
        </SiDialog>
    );
}

export default ProcessDialog;
